import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { trackPageview } from "@/lib/track";
import { LanguageProvider } from "@/context/LanguageContext";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import CursorGlow from "@/components/CursorGlow";
import StickyCTA from "@/components/StickyCTA";
import AITransparency from "@/components/AITransparency";
import Home from "@/pages/Home";
import Trainer from "@/pages/Trainer";
import Kurse from "@/pages/Kurse";
import Einzeltraining from "@/pages/Einzeltraining";
import Onlinecoaching from "@/pages/Onlinecoaching";
import Golfclub from "@/pages/Golfclub";
import TrackManBuchen from "@/pages/TrackManBuchen";
import TrackManSuccess from "@/pages/TrackManSuccess";
import Kontakt from "@/pages/Kontakt";
import Impressum from "@/pages/Impressum";
import AGB from "@/pages/AGB";
import GutscheinKaufen from "@/pages/GutscheinKaufen";
import GutscheinSuccess from "@/pages/GutscheinSuccess";
import AdminLogin from "@/pages/admin/AdminLogin";
import Admin from "@/pages/admin/Admin";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import PaymentPage from "@/pages/PaymentPage";
import StornoPage from "@/pages/StornoPage";
import PromoFunnel from "@/pages/PromoFunnel";
import ShortLinkRedirect from "@/pages/ShortLinkRedirect";
import SchnupperkursFunnel from "@/pages/SchnupperkursFunnel";
import Regelkurs from "@/pages/Regelkurs";
import NachDemSchnupperkurs from "@/pages/NachDemSchnupperkurs";
import TrainingsclubSuccess from "@/pages/TrainingsclubSuccess";
import ProLogin from "@/pages/pro/ProLogin";
import ProLayout from "@/pages/pro/ProLayout";
import ProDashboard from "@/pages/pro/ProDashboard";
import ProCalendar from "@/pages/pro/ProCalendar";
import ProCourses from "@/pages/pro/ProCourses";
import ProCourseDetail from "@/pages/pro/ProCourseDetail";
import ProPlatzreife from "@/pages/pro/ProPlatzreife";
import ProVouchers from "@/pages/pro/ProVouchers";
import ProLinks from "@/pages/pro/ProLinks";
import ProSearch from "@/pages/pro/ProSearch";
import ProPrint from "@/pages/pro/ProPrint";

const Shell = () => {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  const isBare = isAdmin || pathname.startsWith("/pro") || pathname.startsWith("/pay/") || pathname.startsWith("/storno/") || pathname.startsWith("/code/") || pathname.startsWith("/r/") || pathname.startsWith("/schnupperkurs") || pathname.startsWith("/nachdemschnupperkurs");
  useEffect(() => {
    if (!/^\/(admin|pro|pay|storno|r)\//.test(pathname)) trackPageview(pathname);
    // Meta Pixel: PageView bei jedem Client-seitigen Routenwechsel
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", "PageView");
    }
  }, [pathname]);
  return (
    <>
      <ScrollToTop />
      {!isBare && <CursorGlow />}
      {!isBare && <Navbar />}
      <main className="relative z-[2]">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/news" element={<Navigate to="/" replace />} />
          <Route path="/trainer" element={<Trainer />} />
          <Route path="/kurse" element={<Kurse />} />
          <Route path="/einzeltraining" element={<Einzeltraining />} />
          <Route path="/onlinecoaching" element={<Onlinecoaching />} />
          <Route path="/golfclub" element={<Golfclub />} />
          <Route path="/trackman" element={<TrackManBuchen />} />
          <Route path="/trackman/success" element={<TrackManSuccess />} />
          <Route path="/trackman/cancel" element={<TrackManBuchen />} />
          <Route path="/kontakt" element={<Kontakt />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/agb" element={<AGB />} />
          <Route path="/gutschein" element={<GutscheinKaufen />} />
          <Route path="/gutschein/success" element={<GutscheinSuccess />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/pay/:token" element={<PaymentPage />} />
          <Route path="/storno/:token" element={<StornoPage />} />
          <Route path="/code/:code" element={<PromoFunnel />} />
          <Route path="/r/:slug" element={<ShortLinkRedirect />} />
          <Route path="/schnupperkurs" element={<SchnupperkursFunnel />} />
          <Route path="/nachdemschnupperkurs" element={<NachDemSchnupperkurs />} />
          <Route path="/trainingsclub/success" element={<TrainingsclubSuccess />} />
          <Route path="/regelkurs" element={<Regelkurs />} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<Admin />} />
          <Route path="/admin/preisliste" element={<Admin />} />
          <Route path="/pro-login" element={<ProLogin />} />
          <Route path="/pro-dashboard" element={<Navigate to="/pro/dashboard" replace />} />
          <Route path="/pro/print/:sessionId" element={<ProPrint />} />
          <Route path="/pro" element={<ProLayout />}>
            <Route index element={<Navigate to="/pro/dashboard" replace />} />
            <Route path="dashboard" element={<ProDashboard />} />
            <Route path="calendar" element={<ProCalendar />} />
            <Route path="courses" element={<ProCourses />} />
            <Route path="courses/:id" element={<ProCourseDetail />} />
            <Route path="platzreife" element={<ProPlatzreife />} />
            <Route path="gutscheine" element={<ProVouchers />} />
            <Route path="links" element={<ProLinks />} />
            <Route path="search" element={<ProSearch />} />
          </Route>
        </Routes>
      </main>
      {!isBare && <Footer />}
      {!isBare && <div className="h-16 sm:h-[68px]" />}
      {!isBare && <StickyCTA />}
      {!isBare && <AITransparency />}
    </>
  );
};

function App() {
  return (
    <div className="App grain">
      <AuthProvider>
        <LanguageProvider>
          <BrowserRouter>
            <Shell />
            <Toaster position="top-center" theme="dark" />
          </BrowserRouter>
        </LanguageProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
